import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Crown, Trophy, Sparkles, Gem, Users, X } from 'lucide-react'
import confetti from 'canvas-confetti'
import { toast } from 'react-hot-toast'
import audioService from '../../../services/audioService'
import { motion, AnimatePresence } from 'motion/react'
import { hapticNotify } from '../../../utils/haptic'
import ProfileLink from '../../../components/ProfileLink'
import TeamTag from '../../../components/TeamTag'
import useOpenTeam from '../../../hooks/useOpenTeam'

const BattleWinner = ({
  winner,
  totalPrize,
  isCurrentUser = false,
  isParticipant = false,
  skipEffects = false,
  // Итог делёжки банка (`team_result`) — есть только когда победил слот банды
  // с 2+ участниками в батле. Тогда скины не выдаются никому, банк уходит
  // кристаллами всем участникам банды поровну
  teamResult = null,
  // Моя доля (`you_team_payout` / `players[].team_payout`) и флаг «я получил
  // долю»: тиммейта с долей нельзя подписывать как проигравшего
  teamPayout = 0,
  isTeamWin = false,
  primaryActionLabel,
  secondaryActionLabel,
  onPrimaryAction,
  onSecondaryAction,
  onClose
}) => {
  const { t } = useTranslation()
  const openTeam = useOpenTeam()
  const [isOpen, setIsOpen] = useState(!skipEffects)

  // Командный батл: банк поделён, предметы в инвентарь не попали
  const isTeamBattle = Boolean(teamResult)
  // Празднуем и победителю слота, и тиммейту с долей
  const isCelebrating = Boolean(isCurrentUser || isTeamWin)
  const myPayout = Number(teamPayout || 0)

  useEffect(() => {
    if (skipEffects) {
      setIsOpen(false)
    }
  }, [skipEffects])

  const primaryLabel = primaryActionLabel || t('battle.newBattle')
  const secondaryLabel = secondaryActionLabel || t('battle.toBattles')

  const handlePrimaryAction = () => {
    if (typeof onPrimaryAction === 'function') {
      onPrimaryAction()
      return
    }
    setIsOpen(false)
    if (typeof onClose === 'function') onClose()
  }

  const handleSecondaryAction = () => {
    if (typeof onSecondaryAction === 'function') {
      onSecondaryAction()
      return
    }
    setIsOpen(false)
    if (typeof onClose === 'function') onClose()
  }

  useEffect(() => {
    if (!isOpen) return
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [isOpen])

  useEffect(() => {
    // Если skipEffects = true (статус finished), пропускаем все эффекты (звуки и конфетти)
    if (skipEffects) {
      return
    }

    // Воспроизводим звук и показываем toast в зависимости от того, кто победил
    if (isCelebrating) {
      // Победил я или моя банда - Win_Case_V1 и toast "Победа"
      audioService.play('caseBattleWin')
      hapticNotify('success')
      toast.success(isTeamWin ? t('battle.teamWin.title') : t('battle.victory'), {
        duration: 3000,
        icon: '🏆'
      })
    } else if (isParticipant) {
      // Если я участвовал, но проиграл - Lost_Case_V1 и toast "Проиграл"
      audioService.play('caseBattleLost')
      hapticNotify('warning')
      toast.error(t('battle.lost'), {
        duration: 3000
      })
    } else {
      // Если я не участвовал в баттле - upgraderWin
      audioService.play('upgraderWin')
      hapticNotify('success')
    }

    // Показываем конфетти если победил я или моя банда
    if (isCelebrating) {
      const duration = 3000
      const animationEnd = Date.now() + duration
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 10000 }

      const randomInRange = (min, max) => Math.random() * (max - min) + min

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now()

        if (timeLeft <= 0) {
          clearInterval(interval)
          return
        }

        const particleCount = 50 * (timeLeft / duration)

        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
          colors: ['#00FF7F', '#4EFFC6', '#FFD700']
        })
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
          colors: ['#00FF7F', '#4EFFC6', '#FFD700']
        })
      }, 250)

      return () => clearInterval(interval)
    }
  }, [isCelebrating, isTeamWin, isParticipant, skipEffects, t])

  // В командном батле банк уходит кристаллами, а не скинами: подпись суммы и
  // ряда предметов меняется, чтобы скины читались как дроп, а не как выигранное
  const prizeLabel = isTeamBattle ? t('battle.teamWin.bank') : t('battle.totalPrize')
  const winnerTitle = isTeamWin
    ? t('battle.teamWin.title')
    : isTeamBattle
      ? t('battle.teamWin.titleOther')
      : t('battle.help.step4Title')

  /**
   * Итог делёжки: банда, банк, число участников и **личная** доля игрока
   * (`team_payout`, а не `team_result.share` — у слота-победителя она на 1–3
   * кристалла больше).
   */
  const renderTeamBlock = () => {
    if (!isTeamBattle) return null

    const team = teamResult.team || {}
    const bank = Number(teamResult.bank || 0)
    const members = Number(teamResult.count_users || 0)

    return (
      <div className="mt-4 w-full rounded-xl border border-team-accent/40 bg-team-accent/10 p-4">
        <div className="flex items-center gap-3">
          <img
            src={team.ava || `https://ui-avatars.com/api/?name=${encodeURIComponent(team.name || 'Team')}&background=A78BFA&color=0A0A0F`}
            alt={team.name}
            className="h-10 w-10 shrink-0 rounded-xl border-2 border-team-accent object-cover"
            loading="lazy"
          />
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-team-accent">
              {t('battle.teamWin.winnerTeam')}
            </p>
            <p
              onClick={team.slug ? (event) => openTeam(event, team.slug) : undefined}
              className={`truncate font-semibold text-white ${
                team.slug ? 'cursor-pointer transition-transform active:scale-[0.98]' : ''
              }`}
              title={team.name}
            >
              {team.name}
            </p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1 text-xs tabular-nums text-text-secondary">
            <Users className="h-3.5 w-3.5" />
            {members}
          </span>
        </div>

        <p className="mt-2 text-xs text-text-secondary">
          {t('battle.teamWin.split', { bank: bank.toLocaleString(), members })}
        </p>

        {myPayout > 0 && (
          <div className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-accent-primary/30 bg-accent-primary/10 px-3 py-2">
            <span className="text-xs text-text-secondary">{t('battle.teamWin.youGot')}</span>
            <span className="inline-flex items-center gap-1 text-lg font-bold tabular-nums text-accent-primary">
              <Gem className="h-4 w-4" />
              +{myPayout.toLocaleString()}
            </span>
          </div>
        )}

        <p className="mt-2 text-[11px] leading-snug text-text-secondary">
          {t('battle.teamWin.crystalsNote')}
        </p>
      </div>
    )
  }

  const renderInlineWinner = () => (
    <div className="mb-6">
      <h2 className="text-xl font-bold text-white mb-4">{winnerTitle}</h2>

      <div className="relative bg-gradient-to-r from-accent-primary/20 to-accent-secondary/20 border-2 border-accent-primary rounded-lg p-6 mb-4 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(/images/case-battle-winner.jpg)' }}
        />

        <div className="absolute inset-0 bg-dark-primary/80" />

        <div className="relative flex flex-col items-center">
          <Crown className="w-16 h-16 text-yellow-400 mb-3 animate-pulse" />

          {/* Аватар и имя победителя кликабельны: user_slug === null → не кликабелен */}
          <ProfileLink slug={winner.user_slug} className="flex flex-col items-center">
            <img
              src={winner.user_ava || `https://ui-avatars.com/api/?name=${winner.user_name}&background=00FF7F&color=0A0A0F`}
              alt={winner.user_name}
              className="w-20 h-20 rounded-full border-4 border-accent-primary mb-3"
            />

            <p className="text-xl font-bold text-white mb-4">
              {/* В кейсбатлах банда приходит в user_team */}
              <TeamTag team={winner.user_team} />
              {winner.user_name}
            </p>
          </ProfileLink>

          <div className="bg-dark-primary rounded-lg p-4 w-full">
            <div className="flex items-center justify-center gap-2 mb-2">
              <p className="text-text-secondary text-sm">{prizeLabel}</p>
            </div>
            <div className="flex items-center justify-center gap-2 text-3xl font-bold text-accent-primary">
              <Gem className="w-8 h-8" />
              <span>{totalPrize?.toLocaleString()}</span>
            </div>
          </div>

          {renderTeamBlock()}
        </div>
      </div>

      {isTeamBattle && winner.items && winner.items.length > 0 && (
        <p className="mb-2 text-sm font-semibold text-text-secondary">{t('battle.teamWin.dropTitle')}</p>
      )}

      {winner.items && winner.items.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {winner.items.map((item, index) => (
            <div
              key={index}
              className="bg-surface rounded-lg p-3 border border-accent-primary/30 flex flex-col items-center text-center"
            >
              <img
                src={item.img}
                alt={item.name}
                className="w-32 h-32 aspect-square object-contain mb-2 mx-auto"
              />
              <p className="text-white text-xs font-medium text-center mb-1 w-full">{item.name}</p>
              <div className="flex items-center justify-center gap-1 text-accent-primary font-bold text-sm">
                <Gem className="w-4 h-4" />
                <span>{item.price?.toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )

  if (skipEffects) {
    return renderInlineWinner()
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[9999] overflow-hidden bg-dark-primary" 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="absolute inset-0 bg-dark-primary" />

          <div className="absolute inset-0">
            <div
              className="absolute inset-0 bg-cover bg-center opacity-30"
              style={{ backgroundImage: 'url(/images/case-battle-winner.jpg)' }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-dark-primary/40 via-dark-primary/70 to-dark-primary" />
            <div className="absolute -top-24 left-1/2 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-accent-primary/20 blur-3xl" />
            <div className="absolute -bottom-24 left-1/2 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-accent-secondary/20 blur-3xl" />
          </div>

          <div
            className="absolute inset-0 overflow-y-auto overscroll-contain"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="relative min-h-full w-full">
            <button
              onClick={() => {
                setIsOpen(false)
                if (typeof onClose === 'function') onClose()
              }}
              className="fixed right-4 top-4 z-10 h-10 w-10 rounded-full bg-surface/60 text-white hover:bg-surface/80 transition-colors flex items-center justify-center"
              aria-label={t('common.close')}
            >
              <X className="w-6 h-6" />
            </button>

            <div className="w-full px-4 pt-16 pb-32 flex items-start justify-center">
              <motion.div
                className="relative w-full max-w-[620px]"
                initial={{ scale: 0.9, y: 20, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                transition={{ duration: 0.28, ease: [0.34, 1.56, 0.64, 1] }}
              >
                <div className="flex items-center justify-center gap-2 mb-3">
                  {isCelebrating ? (
                    <>
                      <Trophy className="w-6 h-6 text-yellow-400" />
                      <h2 className="text-2xl font-bold text-white">
                        {isTeamWin ? t('battle.teamWin.title') : t('battle.victory')}
                      </h2>
                      <Sparkles className="w-6 h-6 text-yellow-400" />
                    </>
                  ) : (
                    <>
                      <Crown className="w-6 h-6 text-accent-primary" />
                      <h2 className="text-2xl font-bold text-white">{winnerTitle}</h2>
                      <Crown className="w-6 h-6 text-accent-primary" />
                    </>
                  )}
                </div>

                <div className="relative bg-gradient-to-r from-accent-primary/20 to-accent-secondary/20 border-2 border-accent-primary rounded-2xl p-5 overflow-hidden shadow-2xl shadow-accent-primary/20">
                  <div
                    className="absolute inset-0 bg-cover bg-center opacity-30"
                    style={{ backgroundImage: 'url(/images/case-battle-winner.jpg)' }}
                  />

                  <div className="absolute inset-0 bg-dark-primary/80" />

                  <div className="relative">
                    <div className="flex flex-col items-center">
                      <div className="flex items-center justify-center mb-2">
                        <Crown className="w-14 h-14 text-yellow-400 animate-pulse" />
                      </div>

                      {/* Аватар и имя победителя кликабельны: user_slug === null → не кликабелен */}
                      <ProfileLink slug={winner.user_slug} className="flex flex-col items-center">
                        <img
                          src={winner.user_ava || `https://ui-avatars.com/api/?name=${winner.user_name}&background=00FF7F&color=0A0A0F`}
                          alt={winner.user_name}
                          className="w-24 h-24 rounded-full border-4 border-accent-primary shadow-lg shadow-accent-primary/30"
                        />

                        <div className="mt-3 mb-4 flex flex-col items-center">
                          {/* В кейсбатлах банда приходит в user_team. Бейдж —
                              отдельной строкой над ником, как в шапке профиля;
                              при `user_team === null` строки нет вовсе, иначе
                              появился бы пустой отступ над ником. `pl-1` гасит
                              правый отступ бейджа, нужный ему только когда он
                              стоит вплотную перед ником в одну строку */}
                          {winner.user_team && (
                            <TeamTag team={winner.user_team} className="pl-1 text-base font-semibold" />
                          )}
                          <p className="text-2xl font-bold text-white text-center">
                            {winner.user_name}
                          </p>
                        </div>
                      </ProfileLink>

                      <div className="bg-dark-primary/80 backdrop-blur-sm rounded-xl p-4 w-full border border-white/10">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <p className="text-text-secondary text-sm">{prizeLabel}</p>
                        </div>
                        <div className="flex items-center justify-center gap-2 text-3xl font-bold text-accent-primary">
                          <Gem className="w-8 h-8" />
                          <span>{totalPrize?.toLocaleString()}</span>
                        </div>
                      </div>

                      {renderTeamBlock()}
                    </div>

                    {winner.items && winner.items.length > 0 && (
                      <div className="mt-4 max-h-[38vh] overflow-auto">
                        {isTeamBattle && (
                          <p className="mb-2 text-sm font-semibold text-text-secondary">
                            {t('battle.teamWin.dropTitle')}
                          </p>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                          {winner.items.map((item, index) => (
                            <div
                              key={index}
                              className="bg-surface/70 backdrop-blur-sm rounded-xl p-3 border border-accent-primary/30 flex flex-col items-center text-center"
                            >
                              <img
                                src={item.img}
                                alt={item.name}
                                className="w-32 h-32 aspect-square object-contain mb-2 mx-auto"
                              />
                              <p className="text-white text-xs font-medium text-center mb-1 w-full">{item.name}</p>
                              <div className="flex items-center justify-center gap-1 text-accent-primary font-bold text-sm">
                                <Gem className="w-4 h-4" />
                                <span>{item.price?.toLocaleString()}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="fixed bottom-0 left-0 right-0 z-10 px-4 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
              <div className="mx-auto w-full max-w-[620px]">
                <div className="rounded-2xl border border-white/10 bg-dark-primary/95 backdrop-blur-md p-3 shadow-2xl shadow-black/40">
                  <div className="flex gap-3">
                    <button
                      onClick={handlePrimaryAction}
                      className="flex-1 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary rounded-xl font-semibold hover:opacity-90 transition-opacity"
                    >
                      {primaryLabel}
                    </button>
                    <button
                      onClick={handleSecondaryAction}
                      className="flex-1 py-3 bg-surface/70 backdrop-blur-sm text-white rounded-xl font-semibold hover:bg-surface/90 transition-colors border border-white/20"
                    >
                      {secondaryLabel}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
        </motion.div>
      )}
      {!isOpen && renderInlineWinner()}
    </AnimatePresence>
  )
}

export default BattleWinner
